/**
 * UserActivityPage — admin-only forensic view of one user's
 * material activity, grouped into login-bounded sessions.
 *
 * Route: /admin/user-communications/:userId/user_activity
 *
 * Filter UI (Option B per user spec):
 *   - Single dropdown for activity types (multi-select with
 *     category headers)
 *   - Date range dropdown: 7d / 30d / 90d / Custom — Custom
 *     expands inline date pickers
 *   - Bottom-anchored "Summarize this Activity" button
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2, ChevronDown, ChevronUp, Sparkles, Filter, Calendar,
  LogIn, LogOut, Phone, Video, MessageSquare, Send, Bot, ListTodo,
  CalendarClock, Eye as EyeIcon, UserCog, MessageCircle, ShieldCheck,
  KeyRound, Image as ImageIcon, FileText, CheckCircle2, Trash2,
  UserPlus, ShieldAlert, Settings2, BookOpen, Brain, AtSign,
  Mic, X as XIcon, ChevronsUpDown, Check,
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { usePageHeader } from '../context/PageHeaderContext';

// Icon registry — every type the backend can emit. Defaults to
// `Filter` for any forward-compatible types we haven't bound yet.
const TYPE_ICONS = {
  login: LogIn,
  logout: LogOut,
  password_changed: KeyRound,
  mfa_activated: ShieldCheck,
  mfa_deactivated: ShieldAlert,
  recovery_key_regenerated: KeyRound,
  profile_updated: UserCog,
  profile_picture_changed: ImageIcon,
  resume_uploaded: FileText,
  onboarding_step_completed: CheckCircle2,
  team_message_sent: MessageSquare,
  external_text_sent: Send,
  ai_chat_message_sent: Bot,
  call_made: Phone,
  video_call_made: Video,
  voicemail_left: Mic,
  email_sent: AtSign,
  task_created: ListTodo,
  task_completed: CheckCircle2,
  task_deleted: Trash2,
  appointment_booked: CalendarClock,
  appointment_cancelled: Trash2,
  business_card_view: EyeIcon,
  business_card_edited: Settings2,
  feedback_submitted: MessageCircle,
  feedback_fixed: CheckCircle2,
  feedback_resolved: CheckCircle2,
  user_created: UserPlus,
  user_role_changed: UserCog,
  user_password_reset: KeyRound,
  user_deleted: Trash2,
  phone_routing_changed: Phone,
  ai_training_updated: Brain,
  state_settings_updated: Settings2,
  resource_created: BookOpen,
  resource_deleted: Trash2,
  exemption_granted: ShieldCheck,
  exemption_revoked: ShieldAlert,
};

const fmtDate = (iso) => { try { return iso ? new Date(iso).toLocaleString() : ''; } catch { return ''; } };

const fmtDur = (ms) => {
  if (!ms || ms < 1000) return '<1s';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rest = s % 60;
  if (m < 60) return `${m}m ${rest}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const SessionCard = ({ session, typesIndex }) => {
  const [open, setOpen] = useState(false);
  const counts = session.counts || {};
  const headlineEntries = Object.entries(counts).slice(0, 4);
  return (
    <Card className="border border-gray-200 dark:border-[#2a2a2a]" data-testid={`ua-session-${session.login_at || session.session_end}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-[#222] text-left">
        <LogIn className="w-4 h-4 text-[#586D52] flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{session.login_at ? `Logged in · ${fmtDate(session.login_at)}` : `Activity · ${fmtDate(session.session_end)}`}</p>
          <p className="text-xs text-muted-foreground truncate">
            Session length: {fmtDur(session.duration_ms)} · {session.activities?.length || 0} activities
          </p>
          {headlineEntries.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {headlineEntries.map(([k, n]) => (
                <span key={k} className="text-[10px] bg-gray-100 dark:bg-[#1f1f1f] rounded-full px-2 py-0.5">
                  {(typesIndex[k]?.label || k)} ×{n}
                </span>
              ))}
            </div>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground mt-1" /> : <ChevronDown className="w-4 h-4 text-muted-foreground mt-1" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-[#2a2a2a] space-y-2 text-xs">
          {(session.activities || []).map((a, i) => {
            const Icon = TYPE_ICONS[a.type] || Filter;
            return (
              <div key={i} className="flex items-start gap-2 py-1">
                <Icon className="w-3.5 h-3.5 text-[#586D52] flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold leading-snug">{typesIndex[a.type]?.label || a.type}</p>
                  <p className="text-muted-foreground leading-snug break-words">{a.label}</p>
                  <p className="text-[10px] text-muted-foreground">{fmtDate(a.ts)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

// ────────── Filter primitives ──────────

const TypesDropdown = ({ types, selected, onChange, totals }) => {
  // `selected = null` → all; otherwise a Set-like array of ids.
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const isAll = selected === null;
  const selectedCount = isAll ? types.length : selected.length;
  const summary = isAll ? 'All actions' : (selectedCount === 1
    ? (types.find((t) => t.id === selected[0])?.label || '1 action')
    : `${selectedCount} actions`);

  // Group by category for readability.
  const grouped = useMemo(() => {
    const m = new Map();
    types.forEach((t) => {
      const cat = t.category || 'Other';
      if (!m.has(cat)) m.set(cat, []);
      m.get(cat).push(t);
    });
    return Array.from(m.entries());
  }, [types]);

  const toggle = (id) => {
    if (isAll) {
      onChange(types.filter((t) => t.id !== id).map((t) => t.id));
      return;
    }
    const set = new Set(selected);
    if (set.has(id)) set.delete(id); else set.add(id);
    const next = Array.from(set);
    onChange(next.length === types.length ? null : next);
  };

  const isActive = (id) => isAll || selected.includes(id);

  return (
    <div className="relative flex-1 min-w-0" ref={ref} data-testid="ua-types-dropdown">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full h-9 px-3 rounded-xl bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] flex items-center gap-2 text-xs text-left"
        data-testid="ua-types-dropdown-btn"
      >
        <Filter className="w-3.5 h-3.5 text-[#586D52] flex-shrink-0" />
        <span className="flex-1 truncate font-semibold">{summary}</span>
        <ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground" />
      </button>
      {open && (
        <div
          className="absolute left-0 right-0 mt-1 z-30 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] rounded-xl shadow-lg max-h-[55vh] overflow-y-auto"
          data-testid="ua-types-dropdown-menu"
        >
          <div className="px-3 py-2 sticky top-0 bg-white dark:bg-[#1a1a1a] border-b border-gray-100 dark:border-[#2a2a2a] flex items-center gap-2">
            <button
              onClick={() => onChange(null)}
              className={`text-[11px] px-2 py-1 rounded-full font-semibold ${isAll ? 'bg-[#586D52] text-white' : 'bg-gray-100 dark:bg-[#222] text-muted-foreground'}`}
              data-testid="ua-types-select-all"
            >
              Select all
            </button>
            <button
              onClick={() => onChange([])}
              className="text-[11px] px-2 py-1 rounded-full font-semibold bg-gray-100 dark:bg-[#222] text-muted-foreground"
              data-testid="ua-types-clear"
            >
              Clear
            </button>
          </div>
          {grouped.map(([cat, items]) => (
            <div key={cat} className="py-1">
              <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{cat}</div>
              {items.map((t) => {
                const Icon = TYPE_ICONS[t.id] || Filter;
                const active = isActive(t.id);
                const n = totals[t.id] || 0;
                return (
                  <button
                    key={t.id}
                    onClick={() => toggle(t.id)}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-[#222] ${active ? '' : 'opacity-60'}`}
                    data-testid={`ua-type-option-${t.id}`}
                  >
                    <span className={`w-4 h-4 flex items-center justify-center rounded border ${active ? 'bg-[#586D52] border-[#586D52]' : 'border-gray-300 dark:border-[#444]'}`}>
                      {active && <Check className="w-3 h-3 text-white" />}
                    </span>
                    <Icon className="w-3.5 h-3.5 text-[#586D52]" />
                    <span className="flex-1 text-left truncate">{t.label}</span>
                    {n > 0 && <span className="text-[10px] text-muted-foreground">{n}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RangeDropdown = ({ value, onChange, customFrom, customTo, onCustomChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const labelFor = (v) => {
    if (v === 'custom') {
      if (customFrom || customTo) return `${customFrom || '…'} → ${customTo || '…'}`;
      return 'Custom range';
    }
    if (v === 'today') return 'Today';
    return `Last ${v} days`;
  };

  return (
    <div className="relative flex-shrink-0" ref={ref} data-testid="ua-range-dropdown">
      <button
        onClick={() => setOpen((v) => !v)}
        className="h-9 px-3 rounded-xl bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] flex items-center gap-2 text-xs"
        data-testid="ua-range-dropdown-btn"
      >
        <Calendar className="w-3.5 h-3.5 text-[#586D52]" />
        <span className="font-semibold">{labelFor(value)}</span>
        <ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground" />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 z-30 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] rounded-xl shadow-lg w-64 overflow-hidden"
          data-testid="ua-range-dropdown-menu"
        >
          {[
            { id: 'today', label: 'Today' },
            { id: 7, label: 'Last 7 days' },
            { id: 30, label: 'Last 30 days' },
            { id: 90, label: 'Last 90 days' },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => { onChange(opt.id); setOpen(false); }}
              className={`w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-[#222] ${value === opt.id ? 'font-semibold text-[#586D52]' : ''}`}
              data-testid={`ua-range-option-${opt.id}`}
            >
              <span>{opt.label}</span>
              {value === opt.id && <Check className="w-3.5 h-3.5" />}
            </button>
          ))}
          <div className="border-t border-gray-100 dark:border-[#2a2a2a]">
            <button
              onClick={() => onChange('custom')}
              className={`w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-[#222] ${value === 'custom' ? 'font-semibold text-[#586D52]' : ''}`}
              data-testid="ua-range-option-custom"
            >
              <span>Custom range</span>
              {value === 'custom' && <Check className="w-3.5 h-3.5" />}
            </button>
            {value === 'custom' && (
              <div className="px-3 py-2 space-y-2 bg-gray-50 dark:bg-[#0d0d0d]" data-testid="ua-range-custom-panel">
                <label className="block">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider">From</span>
                  <Input type="date" value={customFrom || ''} onChange={(e) => onCustomChange({ from: e.target.value, to: customTo })} className="h-8 text-xs mt-0.5" data-testid="ua-date-from" />
                </label>
                <label className="block">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider">To</span>
                  <Input type="date" value={customTo || ''} onChange={(e) => onCustomChange({ from: customFrom, to: e.target.value })} className="h-8 text-xs mt-0.5" data-testid="ua-date-to" />
                </label>
                {(customFrom || customTo) && (
                  <button
                    onClick={() => onCustomChange({ from: '', to: '' })}
                    className="text-[10px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                    data-testid="ua-date-clear"
                  >
                    <XIcon className="w-3 h-3" /> Clear dates
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ────────── Page ──────────

const UserActivityPage = ({ api, userId, user, SummarizePopupComponent }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30); // 7 | 30 | 90 | 'custom'
  const [customDates, setCustomDates] = useState({ from: '', to: '' });
  const [activeTypes, setActiveTypes] = useState(null); // null = all
  const [summarizeOpen, setSummarizeOpen] = useState(false);

  usePageHeader({
    title: user?.name || 'User',
    back: `/admin/user-communications/${userId}`,
  }, [user?.name, userId]);

  const computedRange = useMemo(() => {
    if (range === 'custom') {
      const f = customDates.from ? new Date(customDates.from + 'T00:00:00').toISOString() : null;
      const t = customDates.to ? new Date(customDates.to + 'T23:59:59').toISOString() : null;
      return { from: f, to: t };
    }
    if (range === 'today') {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      return { from: start.toISOString(), to: now.toISOString() };
    }
    const t = new Date();
    const f = new Date(t.getTime() - range * 24 * 3600 * 1000);
    return { from: f.toISOString(), to: t.toISOString() };
  }, [range, customDates]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = {};
        if (computedRange.from) params.from = computedRange.from;
        if (computedRange.to) params.to = computedRange.to;
        if (activeTypes && activeTypes.length >= 0 && activeTypes.length < 999) {
          // null = no `types` param (server treats as all). Empty
          // array = explicitly nothing selected; we still send `types`
          // so the server returns zero rows (UX matches the filter).
          if (activeTypes.length > 0) params.types = activeTypes;
          else params.types = ['__none__']; // sentinel — server ignores unknown types, returns []
        }
        const { data: d } = await api.get(`/admin/user-activity/${userId}`, { params, paramsSerializer: { indexes: null } });
        if (!cancelled) setData(d);
      } catch (e) { /* silent */ } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, userId, computedRange.from, computedRange.to, activeTypes]);

  // Backend returns available_types as either a list of strings
  // (legacy) OR a list of {id, label, category} dicts (new). Coerce.
  const types = useMemo(() => {
    const raw = data?.available_types || [];
    return raw.map((t) => (typeof t === 'string' ? { id: t, label: t, category: 'Other' } : t));
  }, [data]);

  const typesIndex = useMemo(() => {
    const m = {};
    types.forEach((t) => { m[t.id] = t; });
    return m;
  }, [types]);

  const totals = data?.type_totals || {};

  return (
    <div
      className="fixed inset-x-0 flex flex-col bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] px-3 pt-2"
      style={{ top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))', bottom: 'calc(var(--app-bottom-h, 4rem) + env(safe-area-inset-bottom, 0px))' }}
      data-testid="ua-page"
    >
      {/* Filters: action-type dropdown + date-range dropdown */}
      <div className="flex items-stretch gap-2 mb-3" data-testid="ua-filters">
        <TypesDropdown types={types} selected={activeTypes} onChange={setActiveTypes} totals={totals} />
        <RangeDropdown
          value={range}
          onChange={(v) => { setRange(v); if (v !== 'custom') setCustomDates({ from: '', to: '' }); }}
          customFrom={customDates.from}
          customTo={customDates.to}
          onCustomChange={setCustomDates}
        />
      </div>

      {/* Summary chip — selected count + total visible */}
      <div className="text-[11px] text-muted-foreground mb-2" data-testid="ua-summary">
        {data ? `${data.total_activities || 0} material action${(data.total_activities || 0) === 1 ? '' : 's'} in this window` : ''}
      </div>

      {loading && (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      )}

      {!loading && data && (
        <div className="flex-1 min-h-0 overflow-y-auto pb-2 space-y-2" data-testid="ua-sessions">
          {(data.sessions || []).length === 0 && (
            <p className="text-sm text-muted-foreground py-10 text-center">No activity in this window.</p>
          )}
          {(data.sessions || []).map((s, i) => <SessionCard key={i} session={s} typesIndex={typesIndex} />)}
        </div>
      )}

      {!loading && data && (
        <div className="pt-2 pb-2 mt-auto" data-testid="ua-summarize-bar">
          <Button
            onClick={() => setSummarizeOpen(true)}
            className="w-full h-11 rounded-2xl bg-[#0EA5E9] hover:bg-[#0284C7] text-white"
            data-testid="ua-summarize-btn"
          >
            <Sparkles className="w-4 h-4 mr-1" /> Summarize this Activity
          </Button>
        </div>
      )}

      {SummarizePopupComponent && (
        <SummarizePopupComponent
          open={summarizeOpen}
          onClose={() => setSummarizeOpen(false)}
          api={api}
          payload={{ user_id: userId, kind: 'user_activity' }}
          title="User Activity"
        />
      )}
    </div>
  );
};

export default UserActivityPage;
